import 'leaflet-draw';
import area from '@turf/area';
import { Feature } from 'geojson';
import leaflet, { DrawEvents, DrawMap, DrawOptions, LatLng, LeafletEventHandlerFn } from 'leaflet';
import { SyntheticEvent } from 'react';
import { getSimplifiedGeoJSON } from '../../../../helpers/geometry/polygon.helpers';
import { DRAWING_STATES, MOUSE_BUTTON, MOUSE_EVENTS, MouseLeafletEvent } from '../../annotations.interface';
import { AddRectangleVertexCommand } from '../../undoRedoCommands/undoRedoCommands';
import { IUndoRedoHistory } from '../../undoRedoHistory.service';
import { IDrawer } from './IDrawer';

export class DrawRectangle extends leaflet.Draw.Rectangle implements IDrawer {
  private _startLatLng?: LatLng;
  private _isCurrentlyTwoClickDrawing?: boolean;
  readonly _map!: DrawMap;

  private drawState: DRAWING_STATES = DRAWING_STATES.NOT_DRAWING;
  private readonly onDrawCreated: (geojson: Feature) => void;
  private readonly undoRedoHistory: IUndoRedoHistory;
  public lastMouseMoveEvent: SyntheticEvent | undefined;

  constructor(map: DrawMap, onDrawCreated: (geojson: Feature) => void, undoRedoHistory: IUndoRedoHistory, options?: DrawOptions.RectangleOptions) {
    super(map, { ...options, repeatMode: true });
    this._map = map;
    this.onDrawCreated = onDrawCreated;
    this.undoRedoHistory = undoRedoHistory;
  }

  public enable(): this {
    super.enable();
    this._isDrawing = true;
    this._startLatLng = undefined;
    return this;
  }

  public disable(): this {
    super.disable();
    this._startLatLng = undefined;
    this._isDrawing = true;
    this.undoRedoHistory.clearDrawingHistory();
    return this;
  }

  public disableMouseMarkerFocus(): void {}

  public addHandlers(): void {
    this._map.on(MOUSE_EVENTS.MOUSE_DOWN, this.handleMouseDown);
    this._map.on(MOUSE_EVENTS.MOUSE_UP, this.handleMouseUp);

    this._map.on(leaflet.Draw.Event.DRAWSTOP, this.handleDrawStop);
    this._map.on(leaflet.Draw.Event.CREATED, this.handleDrawCreated as unknown as LeafletEventHandlerFn);
  }

  public removeHandlers(): void {
    this._map.off(MOUSE_EVENTS.MOUSE_DOWN, this.handleMouseDown);
    this._map.off(MOUSE_EVENTS.MOUSE_UP, this.handleMouseUp);

    this._map.off(leaflet.Draw.Event.DRAWSTOP, this.handleDrawStop);
    this._map.off(leaflet.Draw.Event.CREATED, this.handleDrawCreated as unknown as LeafletEventHandlerFn);
  }

  private handleMouseUp = (e: MouseLeafletEvent): void => {
    if (e.originalEvent.button !== MOUSE_BUTTON.LEFT) {
      return;
    }
    switch (this.drawState) {
      case DRAWING_STATES.DRAWING_BY_DRAGGING:
        this.addVertexWrapper(e.latlng);
        this.handleDrawStop();
        break;
      case DRAWING_STATES.NOT_DRAWING:
      case DRAWING_STATES.DRAWING_BY_CLICKING:
        break;
    }
  };

  private handleMouseDown = (e: MouseLeafletEvent): void => {
    if (e.originalEvent.button !== MOUSE_BUTTON.LEFT) {
      return;
    }
    this.addVertexWrapper(e.latlng);
    this.drawState = DRAWING_STATES.DRAWING_BY_DRAGGING;
  };

  private handleDrawStop = (): void => {
    this.drawState = DRAWING_STATES.NOT_DRAWING;
  };

  private addVertexWrapper(latlng: LatLng): void {
    if (!this._isCurrentlyTwoClickDrawing) {
      this.undoRedoHistory.addCommand(new AddRectangleVertexCommand(this, latlng));
    }

    this.addVertex(latlng);
  }

  public addVertex(latlng: LatLng): void {
    if (this._shape) {
      this._fireCreatedEvent();
    }
    if (this._startLatLng) {
      return;
    }
    this._isCurrentlyTwoClickDrawing = true;
    this._isDrawing = true;
    this._startLatLng = latlng;
  }

  _onMouseMove(e: SyntheticEvent | undefined): void {
    this.lastMouseMoveEvent = e;
    if (!this._isCurrentlyTwoClickDrawing) {
      return;
    }
    leaflet.Draw.SimpleShape.prototype._onMouseMove.call(this, e);
  }

  handleDrawCreated = (e: DrawEvents.Created): void => {
    const simplifiedGeojson = getSimplifiedGeoJSON(e.layer);
    if (!simplifiedGeojson) {
      return;
    }
    if (area(simplifiedGeojson) < 1) {
      return;
    }
    this.onDrawCreated(simplifiedGeojson);
  };

  // FIXES some bugs with contextmenu click or different click behavior in browsers
  // See S20-120, S20-133 bugs on JIRA
  _onTouch(): void {}
  _onMouseDown(): void {}
  _onMouseUp(): void {}
}
